import { useParams, Link, Navigate } from 'react-router-dom';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import { SEO } from '../components/SEO';
import { PortalLayout } from '../components/PortalLayout';
import { PortalSidebar } from '../components/PortalSidebar';
import { Breadcrumbs } from '../components/Breadcrumbs';
import { allRules, rulesSidebarGroups } from '../data/rulesContent';

const RuleDetail = () => {
  const { code } = useParams();
  const index = allRules.findIndex((r) => r.code === code);
  const rule = allRules[index];

  if (!rule) return <Navigate to="/dpdp-rules" replace />;

  const prev = allRules[index - 1];
  const next = allRules[index + 1];

  const ruleJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Legislation',
    name: `${rule.code} — ${rule.title}`,
    legislationIdentifier: `DPDP-RULES-2025-${rule.code}`,
    description: rule.detail,
    isPartOf: 'Digital Personal Data Protection Rules, 2025',
  };

  return (
    <>
      <SEO
        title={`${rule.code} DPDP Rules — ${rule.title}`}
        description={rule.detail}
        path={`/dpdp-rules/rule/${rule.code}`}
        jsonLd={ruleJsonLd}
      />
      <PortalLayout
        breadcrumb={<Breadcrumbs trail={[{ label: 'DPDP Rules', href: '/dpdp-rules' }, { label: rule.groupTitle, href: `/dpdp-rules?group=${rule.groupId}` }, { label: rule.code }]} />}
        sidebar={<PortalSidebar heading="DPDP Rules, 2025" headingHref="/dpdp-rules" groups={rulesSidebarGroups} activeItemId={rule.code} defaultOpenGroupId={rule.groupId} />}
      >
        <div data-testid="rule-detail-page" className="max-w-3xl">
          <p className="font-mono text-xs uppercase tracking-[0.2em] text-saffron-600 mb-3">{rule.groupTitle}</p>
          <span className="font-serif text-xl text-gray-400">{rule.code}</span>
          <h1 className="font-serif text-3xl sm:text-4xl text-navy leading-tight mt-1 mb-6">{rule.title}</h1>
          <p className="text-gray-700 text-base sm:text-lg leading-[1.75]" data-testid="rule-detail-body">{rule.detail}</p>

          <div className="flex items-center justify-between gap-4 mt-14 pt-6 border-t border-gray-200" data-testid="rule-detail-nav">
            {prev ? (
              <Link to={`/dpdp-rules/rule/${prev.code}`} data-testid="rule-detail-prev-link" className="flex items-center gap-2 text-gray-500 hover:text-saffron-600 text-sm transition-colors">
                <ArrowLeft size={14} />
                <span>{prev.code} {prev.title}</span>
              </Link>
            ) : <span />}
            {next ? (
              <Link to={`/dpdp-rules/rule/${next.code}`} data-testid="rule-detail-next-link" className="flex items-center gap-2 text-gray-500 hover:text-saffron-600 text-sm text-right transition-colors">
                <span>{next.code} {next.title}</span>
                <ArrowRight size={14} />
              </Link>
            ) : <span />}
          </div>
        </div>
      </PortalLayout>
    </>
  );
};

export default RuleDetail;
